import { Fragment, useLayoutEffect, useRef } from "react";

import { MarkdownText } from "@citrine/atoms/markdown-text";
import type { AgentMessage } from "@citrine/resources/agents";

import { getMessageText } from "#src/components/catalyst/hooks/use-agent-stream";

import * as sx from "./agent-chat-message-list.sx";
import type { AgentOption } from "./agent-welcome";
import { AgentWelcome } from "./agent-welcome";

export function AgentChatMessageList({
  activeAgentKey,
  agents,
  isStreaming,
  messages,
  onSelectAgent,
}: {
  activeAgentKey: string;
  agents: AgentOption[];
  isStreaming: boolean;
  messages: AgentMessage[];
  onSelectAgent: (key: string) => void;
}) {
  const messageListRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const messageList = messageListRef.current;
    if (messageList) {
      messageList.scrollTo({
        behavior: "smooth",
        top: messageList.scrollHeight,
      });
    }
  }, [messages]);

  return (
    <sx.MessageList ref={messageListRef}>
      {messages.length ? null : (
        <AgentWelcome
          activeAgentKey={activeAgentKey}
          agents={agents}
          onSelectAgent={onSelectAgent}
        />
      )}

      {messages.map((message, messageIndex) => {
        const key = `${message.role}-${message.id}`;

        if (message.role === "user") {
          return (
            <sx.MessageBubble key={key} isUser>
              <sx.Text>{getMessageText(message)}</sx.Text>
            </sx.MessageBubble>
          );
        }

        // Errors are system conditions, not the agent's content, so they render
        // as standalone alerts outside the bubble. Text/event parts stay inside.
        const { bubbleParts = [], errorParts = [] } = Object.groupBy(
          message.parts,
          (part) => (part.kind === "error" ? "errorParts" : "bubbleParts"),
        );
        const isLastMessage = messageIndex === messages.length - 1;
        const showStreamingIndicator = isStreaming && isLastMessage;

        return (
          <Fragment key={key}>
            {bubbleParts.length || showStreamingIndicator ? (
              <sx.MessageBubble>
                {bubbleParts.map((part) => (
                  <BubblePart key={`${part.kind}-${part.id}`} part={part} />
                ))}
                {showStreamingIndicator ? <sx.StreamingIndicator /> : null}
              </sx.MessageBubble>
            ) : null}
            {errorParts.map((part) => (
              <sx.ErrorAlert key={`error-${part.id}`} severity="error">
                {part.text}
              </sx.ErrorAlert>
            ))}
          </Fragment>
        );
      })}
    </sx.MessageList>
  );
}

function BubblePart({ part }: { part: AgentMessage["parts"][number] }) {
  return part.kind === "event" ? (
    <sx.EventLine>
      <sx.EventGlyph />
      <sx.Text color="textSecondary" variant="caption">
        {part.text}
      </sx.Text>
    </sx.EventLine>
  ) : part.kind === "text" ? (
    <MarkdownText text={part.text} />
  ) : null;
}
